import qs from 'qs'
import React, { useState } from 'react'
import { useWindowSize } from 'react-use'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import 'react-dates/initialize'

import {
  mainContainerHeaderStyle,
  headerBarLeftWrapperStyle,
  tableHeaderThStyle,
  tableBodyTdStyle,
  tableBodyTdStyleUnread,
} from '@/constants/layout'

import {
  guestNameUnreadEnStyle,
  guestNameUnreadJaStyle,
  guestNameStyle,
  guestNameKanaStyle,
  companionStyle,
  reservationRoomNumbersStyle,
  selfCheckinTimeStyle,
  lowScoreStyle,
  middleScoreStyle,
  HighScoreStyle,
  attentionLogoStyle,
  reloadWrapperStyle,
} from '@/components/pages/accommodation-management/list-layout'

import { Button } from '@/components/atoms/button'
import { ReloadButton } from '@/components/atoms/reload-button'
import { AccommodationPaging } from '@/components/molecules/accommodation-paging'
import { DetailedReserchPanel } from '@/components/pages/accommodation-management/_checkout/detailed-reserch-panel'
import { dividedRoomNumber, dividedTypeName } from '@/libs/divide-list'
import { useSearchedConditions } from '@/hooks/use-search-condition'
import { convertLabelWithEntireSearch, EntireSearchContentType } from '@/models/entire-search'
import { SortableTableHeader, SortKeyType } from '@/components/molecules/sortable-table-header'
import { SortKey } from '../list'
import { SearchReservationList } from '@/models/reservation'

type ContainerProps = {
  reservationList: SearchReservationList[]
  page: number
  setPage: (v: number) => void
  listTotal: number
  limit: number
  pagingIsDisabled: boolean
  onClickReserch: () => void
  onClickClearReserch: () => void
  detailedReserch: EntireSearchContentType
  setDetailedReserch: any
  sortKey: SortKeyType
  onChangeSortKey: (sort: SortKeyType) => void
  listScrollRef?: React.RefObject<HTMLDivElement>
}

export const CheckoutList: React.FC<ContainerProps> = ({
  reservationList,
  page,
  setPage,
  listTotal,
  limit,
  pagingIsDisabled,
  onClickReserch,
  onClickClearReserch,
  detailedReserch,
  setDetailedReserch,
  ...props
}) => {
  const windows = useWindowSize()
  const { t, i18n } = useTranslation()
  const [isDropDownReserchPanelOpen, setIsDropDownReserchPanelOpen] = useState<boolean>(false)
  const { SearchedConditionValues } = useSearchedConditions(convertLabelWithEntireSearch('checkout'))

  return (
    <>
      <div css={[mainContainerHeaderStyle, { minWidth: 'auto' }]}>
        <div css={headerBarLeftWrapperStyle}>
          <div>
            <Button
              buttonType={4}
              width={i18n.language === 'en' ? 118 : 90}
              height={32}
              fontSize={12}
              onClick={() => setIsDropDownReserchPanelOpen(!isDropDownReserchPanelOpen)}
            >
              {t('Detailed search')}
            </Button>
            {isDropDownReserchPanelOpen && (
              <DetailedReserchPanel
                onClickReserch={onClickReserch}
                onClickClearReserch={onClickClearReserch}
                setIsDropDownReserchPanelOpen={() => setIsDropDownReserchPanelOpen(!isDropDownReserchPanelOpen)}
                detailedReserch={detailedReserch}
                setDetailedReserch={setDetailedReserch}
              />
            )}
          </div>
          <div css={reloadWrapperStyle}>
            <ReloadButton onClick={onClickReserch} />
          </div>

          <SearchedConditionValues />
        </div>
        <div css={buttonWrapperStyle}>
          <AccommodationPaging page={page} setPage={setPage} total={listTotal} limit={limit} isDisabled={pagingIsDisabled} />
        </div>
      </div>
      <div ref={props.listScrollRef} css={{ overflow: 'auto', height: windows.height - 218 }}>
        <table css={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortableTableHeader
                name={SortKey.ReserveGuestName}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Guest')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.ReservationId}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Reservation ID')}
              </SortableTableHeader>
              <SortableTableHeader name={SortKey.RoomType} onChange={props.onChangeSortKey} value={props.sortKey} css={tableHeaderThStyle}>
                {t('Room type')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.RoomNumber}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Room number')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.Evaluation}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Evaluation')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.CheckoutDate}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Check-out date')}
              </SortableTableHeader>
              <SortableTableHeader name={SortKey.UpdatedAt} onChange={props.onChangeSortKey} value={props.sortKey} css={tableHeaderThStyle}>
                {t('Update date')}
              </SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {reservationList?.map((v, index) => {
              return (
                <Link
                  to={{
                    pathname: `/checkout/${v.checkinId}`,
                    search: qs.stringify({ reservationId: v.reservationId }),
                  }}
                  key={index}
                  css={[tableBodyTdStyleUnread, { display: 'table-row', ':hover': { color: '#000' } }]}
                >
                  <td css={[tableBodyTdStyle, { paddingLeft: 32 }]}>
                    <div css={() => (i18n.language === 'en' ? guestNameUnreadEnStyle : guestNameUnreadJaStyle)}>
                      <div css={guestNameKanaStyle}>{v.guestNameKana ? v.guestNameKana : '-'}</div>
                      <div css={guestNameStyle}>{v.guestName ? v.guestName : '-'}</div>
                    </div>
                  </td>
                  <td css={reservationIdTdStyle}>
                    <div css={companionStyle}>{v.reservationId ? v.reservationId : '-'}</div>
                  </td>
                  <td css={[tableBodyTdStyle, { maxWidth: 200 }]}>
                    <div css={reservationRoomNumbersStyle}>{dividedTypeName(v.rooms)}</div>
                  </td>
                  <td css={[tableBodyTdStyle, { maxWidth: 130 }]}>
                    <div css={[reservationRoomNumbersStyle, { width: '100%' }]}>{dividedRoomNumber(v.rooms)}</div>
                  </td>
                  <td css={[tableBodyTdStyle]}>
                    <div css={{ display: 'flex', alignItems: 'center' }}>
                      <div
                        css={[
                          (() => {
                            if (v.point !== null && v.point !== undefined && Number(v.point) <= 6) {
                              return lowScoreStyle
                            }
                            if (v.point !== null && v.point !== undefined && Number(v.point) <= 8) {
                              return middleScoreStyle
                            }
                            if (v.point !== null && v.point !== undefined && Number(v.point) <= 10) {
                              return HighScoreStyle
                            }
                          })(),
                          { justifyContent: 'start' },
                        ]}
                      >
                        {v.point ?? '-'}
                      </div>
                    </div>
                  </td>
                  <td css={[tableBodyTdStyle]}>
                    <div css={selfCheckinTimeStyle}>{v.checkoutDate ? dayjs(v.checkoutDate).format(t('YYYY-MM-DD')) : '-'}</div>
                  </td>
                  <td css={[tableBodyTdStyle, { maxWidth: 175 }]}>
                    {v.approvedStatusUpdatedAtUnixtime ? dayjs.unix(v.approvedStatusUpdatedAtUnixtime).format(t('MM-DD HH:mm')) : '-'}
                  </td>
                </Link>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

const buttonWrapperStyle = css({
  display: 'flex',
})

const reservationIdTdStyle = css(tableBodyTdStyle, {
  maxWidth: 180,
  '@media(max-width: 1080px)': {
    maxWidth: 140,
  },
})
